import { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Navbar, Nav, Container, Badge } from 'react-bootstrap';
import {
  IconHome,
  IconDatabase,
  IconBox,
  IconPhoto,
  IconPlayerPlay,
  IconPlugConnected,
  IconRobot,
  IconTools,
  IconChevronDown,
  IconActivity,
  IconSparkles,
  IconMail,
  IconLink,
  IconClock,
  IconMessageCircle
} from '@tabler/icons-react';

// Importar componentes existentes
import ProductsList from '../../Products';
import Playground from '../../../jsx/components/Playground';
import UnifiedKnowledgeTraining from '../../../jsx/components/UnifiedKnowledgeTraining';
import AutomationsList from '../../Automations/AutomationsList';
import ScheduledFollowupsList from '../../Automations/ScheduledFollowupsList';

// Importar novos componentes do AgentHub
import Dashboard from './Dashboard';
import Integrations from './Integrations';
import ImageLibrary from './ImageLibrary';
import WaPublicLink from './WaPublicLink';
import LeadFollowup from './LeadFollowup';
import TimeRangeMessage from './TimeRangeMessage';
import Templates from '../../Templates';

import TabWrapper from './TabWrapper';
import { useAgentHubContext } from '../contexts/AgentHubContext';
import UserContext from '../../../contexts/UserContext';

// Componente customizado para dropdown com portal
const PortalDropdown = ({ title, id, className, show, onToggle, children }) => {
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const updatePosition = () => {
      if (dropdownRef.current && show) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX
        });
      }
    };

    if (show) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const dropdown = document.querySelector(`.portal-dropdown-menu[data-dropdown="${id}"]`);
        if (dropdown && !dropdown.contains(event.target)) {
          onToggle(false);
        }
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onToggle, id]);

  const dropdownMenu = show && (
    <div
      className={`portal-dropdown-menu ${className || ''}`}
      data-dropdown={id}
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        right: 0,
        zIndex: 9999,
        background: 'white',
        borderRadius: '12px',
        padding: '0.5rem',
        marginTop: '0.5rem',
        minWidth: '320px',
        maxWidth: '400px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
      }}
    >
      {children}
    </div>
  );

  return (
    <>
      <Nav.Link
        ref={dropdownRef}
        className={`${className || ''} dropdown-toggle`}
        onClick={() => onToggle(!show)}
        style={{ cursor: 'pointer' }}
      >
        {title}
      </Nav.Link>
      {typeof document !== 'undefined' && createPortal(dropdownMenu, document.body)}
    </>
  );
};

// Componentes para itens do dropdown
const DropdownItem = ({ active, onClick, className, children }) => (
  <div
    className={`dropdown-item ${className || ''} ${active ? 'active' : ''}`}
    onClick={onClick}
    style={{ cursor: 'pointer' }}
  >
    {children}
  </div>
);

const DropdownHeader = ({ className, children }) => (
  <div className={`dropdown-header ${className || ''}`}>
    {children}
  </div>
);

const DropdownDivider = () => (
  <hr className="dropdown-divider" />
);

// Estrutura organizada da navbar
const NAVBAR_STRUCTURE = {
  inicio: {
    key: "inicio",
    title: "Início",
    icon: IconHome,
    color: "var(--primary)",
    items: [
      {
        key: "dashboard",
        title: "Dashboard",
        icon: IconActivity,
        component: Dashboard,
        description: "Visão geral e métricas do agente"
      }
    ]
  },
  conteudo: {
    key: "conteudo", 
    title: "Conteúdo",
    icon: IconDatabase,
    color: "var(--info)",
    items: [
      {
        key: "knowledge",
        title: "Treinamento",
        icon: IconDatabase,
        component: UnifiedKnowledgeTraining,
        description: "Gerencie documentos e treine o agente",
        priority: true
      },
      {
        key: "products", 
        title: "Produtos",
        icon: IconBox,
        component: ProductsList,
        description: "Catálogo de produtos e treinamento RAG",
        priority: true
      },
      {
        key: "images",
        title: "Biblioteca de Imagens", 
        icon: IconPhoto,
        component: ImageLibrary,
        description: "Gerencie a biblioteca de imagens do agente"
      }
    ]
  },
  ferramentas: {
    key: "ferramentas",
    title: "Ferramentas",
    icon: IconTools, 
    color: "var(--success)",
    groups: [
      {
        title: "Teste & Validação",
        items: [
          {
            key: "playground",
            title: "Playground",
            icon: IconPlayerPlay,
            component: Playground,
            description: "Teste e valide o agente em tempo real",
            priority: true
          },
          {
            key: "wa-public-link",
            title: "Link WhatsApp",
            icon: IconLink,
            component: WaPublicLink,
            description: "Gerencie links públicos para conexão WhatsApp",
            priority: false
          }
        ]
      },
      {
        title: "Automação",
        items: [
          {
            key: "automations",
            title: "Automações",
            icon: IconRobot,
            component: AutomationsList,
            description: "Gerencie automações do agente"
          },
          {
            key: "lead-followup",
            title: "Follow-up de Leads",
            icon: IconMessageCircle,
            component: LeadFollowup,
            description: "Configure mensagens automáticas para leads que somem",
            priority: true
          },
          {
            key: "time-range-message",
            title: "Mensagens por Horário",
            icon: IconClock,
            component: TimeRangeMessage,
            description: "Mensagens automáticas em janelas de horário definidas",
            priority: false
          },
          {
            key: "scheduled-followups",
            title: "Follow-ups Clientes",
            icon: IconClock,
            component: ScheduledFollowupsList,
            description: "Visualize e gerencie follow-ups agendados de clientes"
          },
          {
            key: "templates",
            title: "Templates",
            icon: IconMail,
            component: Templates,
            description: "Gerencie templates de mensagens para automações"
          }
        ]
      },
      {
        title: "Conectividade",
        items: [
          {
            key: "integrations", 
            title: "Integrações",
            icon: IconPlugConnected,
            component: Integrations,
            description: "Conecte o agente com sistemas externos"
          }
        ]
      }
    ]
  }
};

const AgentNavbar = () => {
  const { activeTab, setActiveTab, agentContext } = useAgentHubContext();
  const { isSubaccount } = useContext(UserContext);
  const [expandedMenu, setExpandedMenu] = useState(null);

  // Encontrar item ativo atual em toda a estrutura
  const findActiveItem = () => {
    for (const section of Object.values(NAVBAR_STRUCTURE)) {
      if (section.items) {
        const found = section.items.find(item => item.key === activeTab);
        if (found) return { section, item: found };
      }
      if (section.groups) {
        for (const group of section.groups) {
          const found = group.items.find(item => item.key === activeTab);
          if (found) return { section, item: found, group };
        }
      }
    }
    return { section: NAVBAR_STRUCTURE.inicio, item: NAVBAR_STRUCTURE.inicio.items[0] };
  };

  const { section: activeSection, item: activeItem } = findActiveItem();

  // Obter todos os itens para renderização de conteúdo
  const getAllItems = () => {
    const items = [];
    Object.values(NAVBAR_STRUCTURE).forEach(section => {
      if (section.items) {
        items.push(...section.items);
      }
      if (section.groups) {
        section.groups.forEach(group => {
          // Filtrar itens baseado no tipo de conta
          const filteredItems = group.items.filter((item) => {
            // Se for subconta, filtrar item "wa-public-link"
            if (isSubaccount() && item.key === 'wa-public-link') {
              return false;
            }
            return true;
          });
          items.push(...filteredItems);
        });
      }
    });
    return items;
  };

  const allItems = getAllItems();

  // Renderizar dropdown para seção com grupos
  const renderGroupedDropdown = (section) => (
    <PortalDropdown
      title={
        <span className="navbar-section-title">
          <section.icon size={16} className="me-2" />
          {section.title}
          <IconChevronDown size={14} className="ms-1" />
        </span>
      }
      id={`nav-dropdown-${section.key}`}
      className="agent-nav-dropdown"
      show={expandedMenu === section.key}
      onToggle={(isOpen) => setExpandedMenu(isOpen ? section.key : null)}
    >
      {section.groups.map((group, groupIndex) => (
        <div key={groupIndex}>
          {groupIndex > 0 && <DropdownDivider />}
          <DropdownHeader className="dropdown-group-header">
            <IconSparkles color='var(--warning)' size={12} className="me-2" />
            {group.title}
          </DropdownHeader>
          {group.items
            .filter((item) => {
              // Se for subconta, filtrar item "wa-public-link"
              if (isSubaccount() && item.key === 'wa-public-link') {
                return false;
              }
              return true;
            })
            .map((item) => (
            <DropdownItem
              key={item.key}
              active={activeTab === item.key}
              onClick={() => {
                setActiveTab(item.key);
                setExpandedMenu(null);
              }}
              className="dropdown-nav-item"
            >
              <div className="dropdown-item-content">
                <div className="dropdown-item-header">
                  <item.icon size={16} className="me-2" />
                  <span className="dropdown-item-title">{item.title}</span>
                  
                </div>
                <div className="dropdown-item-description">
                  {item.description}
                </div>
              </div>
            </DropdownItem>
          ))}
        </div>
      ))}
    </PortalDropdown>
  );

  // Renderizar dropdown simples
  const renderSimpleDropdown = (section) => (
    <PortalDropdown
      title={
        <span className="navbar-section-title">
          <section.icon size={16} className="me-2" />
          {section.title}
          <IconChevronDown size={14} className="ms-1" />
        </span>
      }
      id={`nav-dropdown-${section.key}`}
      className="agent-nav-dropdown"
      show={expandedMenu === section.key}
      onToggle={(isOpen) => setExpandedMenu(isOpen ? section.key : null)}
    >
      {section.items.map((item) => (
        <DropdownItem
          key={item.key}
          active={activeTab === item.key}
          onClick={() => {
            setActiveTab(item.key);
            setExpandedMenu(null);
          }}
          className="dropdown-nav-item"
        >
          <div className="dropdown-item-content">
            <div className="dropdown-item-header">
              <item.icon size={16} className="me-2" />
              <span className="dropdown-item-title">{item.title}</span>
              {item.priority && (
                <Badge bg="warning" size="sm" className="ms-2">
                  <IconSparkles color='white' size={10} />
                </Badge>
              )}
            </div>
            <div className="dropdown-item-description">
              {item.description}
            </div>
          </div>
        </DropdownItem>
      ))}
    </PortalDropdown>
  );

  return (
    <div id="agent-navbar-wrapper" className="agent-navbar-container">
      {/* Navbar Principal */}
      <Navbar expand="lg" className="agent-navbar" variant="light">
        <Container fluid>
          {/* Brand/Agent Info */}
          <Navbar.Brand className="agent-brand">
            <div className="agent-info">
              <div className="agent-avatar">
                <IconRobot size={24} />
              </div>
              <div className="agent-details d-none d-md-block">
                <div className="agent-name">
                  {agentContext?.name || "Agente"}
                </div>
                <div className="current-section">
                  {activeSection.title} › {activeItem.title}
                </div>
              </div>
            </div>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="agent-navbar-nav" />

          <Navbar.Collapse id="agent-navbar-nav">
            <Nav className="ms-auto">
              {/* Início - Link direto */}
              <Nav.Link
                active={activeTab === "dashboard"}
                onClick={() => setActiveTab("dashboard")}
                className="navbar-nav-link"
              >
                <IconHome size={16} className="me-2" />
                Início
              </Nav.Link>

              {/* Conteúdo - Dropdown simples */}
              {renderSimpleDropdown(NAVBAR_STRUCTURE.conteudo)}

              {/* Ferramentas - Dropdown com grupos */}
              {renderGroupedDropdown(NAVBAR_STRUCTURE.ferramentas)}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Conteúdo das Tabs */}
      <div className="tab-content-container mt-3">
        {allItems.map((item) => {
          if (activeTab !== item.key) return null;

          if (!item.component) {
            return (
              <div key={item.key} className="empty-state">
                <div className="empty-state-icon">
                  <item.icon size={48} />
                </div>
                <h3 className="empty-state-title">Módulo em Desenvolvimento</h3>
                <p className="empty-state-description">{item.description}</p>
                <div className="empty-state-alert">
                  <IconSparkles
                    color="var(--warning)"
                    className="me-2"
                    size={16}
                  />
                  <strong>Novidade em breve!</strong>
                  <br />
                  Este módulo está sendo desenvolvido e estará disponível na
                  próxima atualização.
                </div>
              </div>
            );
          }

          return (
            <div key={item.key} className="active-tab-content">
              <TabWrapper>
                <item.component />
              </TabWrapper>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default AgentNavbar;